import { UserCurrency } from '@/types/common'
import { CreditCardResponse } from '@/types/creditCard'
import { InvoiceProfile } from '@/types/invoiceProfile'
import { User } from '@/types/user'
import { VirtualPaymentAutomation } from '@/types/virtualPaymentAutomation'

import { MinimalTrip, PaymentInfo, TimeOptions, TimePeriodDetailed, TripProductType } from './index'
import { WorldlineOrderStatus } from './worldline'

export type ServiceTypeFilter = {
	[key in ServiceFeeType]: boolean
}

export interface FeesFilters {
	period: TimeOptions | TimePeriodDetailed
	payment_fee_type: PaymentFeeType | null
	country: {
		[key: number]: boolean
	}
	invoice_profile: {
		[key: number]: boolean
	}
}

// eslint-disable-next-line no-restricted-syntax
export enum ServiceFeeType {
	Trip = 'trip',
	SupportRequest = 'support_request',
	SubscriptionPayment = 'subscription_payment',
	ProfiledUser = 'profiled_user',
	ActiveUser = 'active_user',
	VirtualPaymentAutomation = 'vpa',
}

// eslint-disable-next-line no-restricted-syntax
export enum PaymentFeeType {
	WithFee = 'with_fee',
	WhithoutFee = 'without_fee',
	UnpaidFees = 'unpaid',
}

export type FeeInfo<T extends FeesData> = {
	[key in ServiceFeeType]: T
}

export type Fee<T extends FeesData> = FeeInfo<T> & {
	service_type: ServiceFeeType
}

export type FeesData = BaseFee & Partial<SupportRequestFee & TripFee & PackagePaymentFee & UserFee & ActiveUserFee>

export interface WithBaseFeeStatus {
	status: WorldlineOrderStatus | null
}

interface BaseFee extends WithBaseFeeStatus {
	id: number
	total_tax: number
	total_price: number
	fee_type: string
	created_dt: string
	currency: UserCurrency
}

export interface FeePaymentDetails {
	credit_card: CreditCardResponse | null
	virtual_payment_automation: VirtualPaymentAutomation | null
	charge_dt: string | null
	refunded_by: User | null
	refund_dt: string | null
}

export interface SupportRequestFee extends BaseFee, FeePaymentDetails {
	trip_id: number | null
	not_applicable: boolean
	request_by_user: User
	service: string
	ticket: number
	category: string
	created_by_user: User
	type: string
	billable: boolean
}

export interface TripFee extends BaseFee, FeePaymentDetails {
	trip: MinimalTrip | null
	user: User
	user_id: number
}

export interface VirtualPaymentAutomationFee extends TripFee {
	product_type: TripProductType
	bs_info: {
		service_name: string
		service_dates: string
	} | null
	start_date: string
}

export interface ActiveUserFee extends BaseFee, FeePaymentDetails {
	users_number: number
	invoice_profile: InvoiceProfile
	next_payment_date: string
	remaining_price: number
	remaining_tax: number
}

export interface UserFee extends BaseFee {
	request_by_user: User
	trip_id: number | null
	is_active: boolean
}

export interface PackagePaymentFee extends BaseFee {
	payment_info: PaymentInfo | null
	is_annual: boolean
	next_payment_date: string
}
